/**
 * VIP VAPE — Cart Upsell Engine v2 (UpsellPlus parity)
 * ---------------------------------------------------------------
 * Surfaces:
 *   - Empty cart drawer + cart page (bestsellers)
 *   - Active cart drawer + cart page (FBT + shipping bar + free gift bar)
 *   - PDP "Frequently bought together"
 *   - Post-add-to-cart pop-up modal
 *   - Thank-you / order page upsell
 *
 * Logic: Hybrid (Shopify Recommendations API + manual fallback by product type)
 * Analytics: Wired to VVAnalytics for impression/click/add events
 * A/B Testing: Uses VVAnalytics.getVariant for deterministic bucketing
 * ---------------------------------------------------------------
 */
(function () {
  'use strict';

  // ==================== Settings (aligned with vv-config.liquid keys) ====================
  const SETTINGS = window.VV_SETTINGS || {};
  // Accept either snake_case (current vv-config.liquid emitter) or camelCase (legacy).
  function pick(snake, camel, fallback) {
    if (SETTINGS[snake] != null) return SETTINGS[snake];
    if (SETTINGS[camel] != null) return SETTINGS[camel];
    return fallback;
  }
  const CONFIG = {
    freeShippingThresholdCents: pick('free_shipping_threshold', 'freeShippingCents', 8000),
    freeGiftThresholdCents:     pick('free_gift_threshold',     'freeGiftCents',     15000),
    freeGiftVariantId:          pick('free_gift_variant_id',    'freeGiftVariantId', null) || null,
    freeGiftTitle:              pick('free_gift_title',         'freeGiftTitle',     'Free Gift'),
    pdpFbtEnabled:              pick('pdp_fbt_enable',          'pdpFbtEnabled',     true) !== false,
    pdpFbtHeading:              pick('pdp_fbt_heading',         'pdpFbtHeading',     'Frequently bought together'),
    popupEnabled:               pick('popup_enable',            'popupEnabled',      true) !== false,
    popupHeading:               pick('popup_heading',           'popupHeading',      'Added to cart \u2014 customers also love'),
    popupCta:                   pick('popup_cta',               'popupCta',          'Add to order'),
    popupCooldownMin:           pick('popup_cooldown_min',      'popupCooldownMin',  30),
    variantUpgradeEnabled:      pick('variant_upgrade_enable',  'variantUpgradeEnabled', true) !== false,
    abExperimentEnabled:        pick('ab_test_enable',          'abEnabled',         true) !== false,
    moneyFormat: (window.theme && window.theme.settings && window.theme.settings.moneyFormat) || '${{amount}}',
    maxSuggestions: 6,
    apiLimit: 12,
  };

  // ==================== Network cache (de-dupes parallel calls, caches for session) ====================
  const _netCache = new Map();
  function cachedFetchJSON(url) {
    if (_netCache.has(url)) return _netCache.get(url);
    const p = fetchJSONRaw(url).catch(() => null);
    _netCache.set(url, p);
    return p;
  }

  const CATEGORY_RULES = {
    // Disposables are self-contained — customers typically buy more disposables
    // (different flavours) rather than bare batteries / coils / juices.
    'DISPOSABLES': { complementary_types: ['DISPOSABLES'], cross_sell_types: ['DISPOSABLES'], message: 'Try another flavour' },
    'CLOSED PODS': { complementary_types: ['CLOSED POD DEVICE', 'CLOSED POD STARTER KIT'], cross_sell_types: ['CLOSED PODS'], message: 'Customers also bought' },
    'CLOSED POD DEVICE': { complementary_types: ['CLOSED PODS'], cross_sell_types: [], message: 'Complete your setup with' },
    'CLOSED POD STARTER KIT': { complementary_types: ['CLOSED PODS'], cross_sell_types: [], message: 'Stock up on pods' },
    'VAPE JUICES': { complementary_types: ['COIL REPLACEMENTS', 'ACCESSORIES'], cross_sell_types: ['VAPE JUICES'], message: 'Pairs perfectly with' },
    'OPEN POD KIT': { complementary_types: ['OPEN POD REPLACEMENTS', 'COIL REPLACEMENTS', 'VAPE JUICES', 'ACCESSORIES'], cross_sell_types: [], message: 'Complete your kit with' },
    'OPEN POD REPLACEMENTS': { complementary_types: ['VAPE JUICES', 'COIL REPLACEMENTS'], cross_sell_types: ['OPEN POD REPLACEMENTS'], message: 'Customers also buy' },
    'COIL REPLACEMENTS': { complementary_types: ['VAPE JUICES', 'OPEN POD REPLACEMENTS'], cross_sell_types: ['COIL REPLACEMENTS'], message: 'Pair with' },
    'MOD POD KIT': { complementary_types: ['COIL REPLACEMENTS', 'VAPE JUICES', 'REPLACEMENT TANK', 'ACCESSORIES'], cross_sell_types: [], message: 'Complete your mod with' },
    'REPLACEMENT TANK': { complementary_types: ['COIL REPLACEMENTS', 'VAPE JUICES'], cross_sell_types: [], message: "Don't forget" },
    'REFILLABLE STARTER KIT': { complementary_types: ['VAPE JUICES', 'COIL REPLACEMENTS', 'ACCESSORIES'], cross_sell_types: [], message: 'Get started with' },
    'ACCESSORIES': { complementary_types: ['DISPOSABLES', 'CLOSED PODS', 'VAPE JUICES'], cross_sell_types: ['ACCESSORIES'], message: 'Customers also bought' },
  };

  // ==================== Utilities ====================
  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    return CONFIG.moneyFormat.replace(/\{\{\s*amount\s*\}\}/g, (cents / 100).toFixed(2))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100));
  }
  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[c]));
  }
  async function fetchJSONRaw(url) {
    try {
      const r = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!r.ok) return null;
      return await r.json();
    } catch (e) { return null; }
  }
  // Public alias; routes through cache for GET idempotent endpoints.
  function fetchJSON(url) { return cachedFetchJSON(url); }
  // Bypass cache for cart state (mutates often).
  async function fetchCart() { return (await fetchJSONRaw('/cart.js')) || { items: [], total_price: 0 }; }

  // ==================== A/B Testing ====================
  function abVariant() {
    // 'fallback_only' was removed in v2.5: the Shopify /search/suggest.json
    // endpoint never populates `variants[]` in its response, so any card built
    // purely from that source had no variant id and was falsely rendered as
    // "Sold out". We now only experiment between hybrid and api_only; the
    // enriched-fallback path below patches in real variant ids only when an
    // API path doesn't return enough candidates.
    if (!CONFIG.abExperimentEnabled || !window.VVAnalytics) return 'hybrid';
    return window.VVAnalytics.getVariant('upsell_strategy_v1', ['hybrid', 'api_only']);
  }

  // ==================== Recommendations ====================
  async function fetchRecommendations(productId, intent) {
    const url = `/recommendations/products.json?product_id=${productId}&limit=${CONFIG.apiLimit}&intent=${intent || 'related'}`;
    const data = await fetchJSON(url);
    return (data && data.products) || [];
  }

  // Enrich a product object that lacks a variants[] array by fetching
  // /products/{handle}.js (full Shopify product JSON). Cached via _netCache.
  // Used to patch suggest.json results which never include variants.
  async function enrichWithVariants(p) {
    if (p && p.variants && p.variants.length > 0 && p.variants[0] && p.variants[0].id) return p;
    if (!p || !p.handle) return p;
    const full = await fetchJSON(`/products/${p.handle}.js`);
    if (!full || !full.variants || !full.variants.length) return p;
    // Merge: keep suggest.json title/image but use the full product's variants + available flag.
    return Object.assign({}, p, {
      variants: full.variants,
      available: typeof full.available === 'boolean' ? full.available : p.available,
      product_type: p.product_type || full.product_type || '',
      price: (full.variants[0] && full.variants[0].price) || p.price,
    });
  }

  async function fetchFallbackByCategory(productType, excludeIds) {
    const rule = CATEGORY_RULES[(productType || '').toUpperCase()];
    if (!rule) return [];
    // PERF: fire all category searches in PARALLEL instead of sequentially.
    const calls = rule.complementary_types.map(type => {
      const q = encodeURIComponent(`product_type:"${type}"`);
      return fetchJSON(`/search/suggest.json?q=${q}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`);
    });
    const responses = await Promise.all(calls);
    const raw = [];
    const seen = new Set(excludeIds || []);
    for (const data of responses) {
      const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
      for (const p of products) {
        if (!seen.has(p.id)) {
          raw.push(p);
          seen.add(p.id);
          if (raw.length >= CONFIG.maxSuggestions) break;
        }
      }
      if (raw.length >= CONFIG.maxSuggestions) break;
    }
    // Enrich each suggest.json result with /products/{handle}.js so we have
    // real variant ids — otherwise productCardHTML falls back to "Sold out".
    // Done in parallel; failures degrade gracefully (we keep the bare product).
    const enriched = await Promise.all(raw.map(p => enrichWithVariants(p).catch(() => p)));
    return enriched.filter(p => p && p.available !== false);
  }

  async function getRecommendations(anchorId, anchorType, excludeIds) {
    const variant = abVariant();
    const excludeSet = new Set(excludeIds || []);
    const seen = new Set();
    const dedupePush = (arr, p) => { if (!seen.has(p.id) && !excludeSet.has(p.id)) { seen.add(p.id); arr.push(p); } };

    if (variant === 'fallback_only') {
      const fb = await fetchFallbackByCategory(anchorType, excludeIds);
      const out = [];
      filterByAllowedTypes(fb, anchorType).forEach(p => dedupePush(out, p));
      return out.filter(p => p.available !== false).slice(0, CONFIG.maxSuggestions);
    }

    // PERF: fire complementary + related in PARALLEL (Shopify's complementary index
    // is often empty for catalogs without Search & Discovery rules — don't wait sequentially).
    const apiCalls = [fetchRecommendations(anchorId, 'complementary'), fetchRecommendations(anchorId, 'related')];

    if (variant === 'api_only') {
      const [comp, rel] = await Promise.all(apiCalls);
      const out = [];
      filterByAllowedTypes(comp, anchorType).forEach(p => dedupePush(out, p));
      filterByAllowedTypes(rel, anchorType).forEach(p => dedupePush(out, p));
      return out.filter(p => p.available !== false).slice(0, CONFIG.maxSuggestions);
    }

    // hybrid (default): parallel API + (lazily) category fallback only if API short
    const [comp, rel] = await Promise.all(apiCalls);
    const out = [];
    filterByAllowedTypes(comp, anchorType).forEach(p => dedupePush(out, p));
    if (out.length < CONFIG.maxSuggestions) {
      filterByAllowedTypes(rel, anchorType).forEach(p => dedupePush(out, p));
    }
    if (out.length < CONFIG.maxSuggestions) {
      const fallback = await fetchFallbackByCategory(anchorType, excludeIds);
      fallback.forEach(p => dedupePush(out, p));
    }
    // Drop explicitly-unavailable products so we never paint a "Sold out" card.
    return out.filter(p => p.available !== false).slice(0, CONFIG.maxSuggestions);
  }

  // ---------- View history (localStorage) ----------
  const VIEW_HISTORY_KEY = 'vv_view_history_v1';
  const VIEW_HISTORY_MAX = 10;

  function getViewHistory() {
    try {
      const raw = localStorage.getItem(VIEW_HISTORY_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }
  function pushViewHistory(productId, productType) {
  	if (!productId) return;
  	try {
  	  const list = getViewHistory().filter(e => String(e.id) !== String(productId));
  	  list.unshift({ id: String(productId), type: productType || '', t: Date.now() });
  	  localStorage.setItem(VIEW_HISTORY_KEY, JSON.stringify(list.slice(0, VIEW_HISTORY_MAX)));
  	} catch (e) {}
  }

  // ---------- Dynamic empty-cart suggestions ----------
  // Strategy (in order):
  //   1. Personalized: Shopify /recommendations against the most recently viewed
  //      product (true "you also viewed" signal).
  //   2. Bestsellers: /collections/{handle}/products.json?sort_by=best-selling
  //      tries common bestseller collection handles, then falls back to all.
  //   3. Newest: /products.json (last-resort cold-start).
  // Each source is filtered to in-stock items, deduped, and trimmed to maxSuggestions.
  async function getBestsellers() {
    const seen = new Set();
    const out = [];
    const limit = CONFIG.maxSuggestions;

    const accept = (p) => {
      if (!p || p.available === false) return;
      const id = String(p.id || '');
      if (!id || seen.has(id)) return;
      seen.add(id);
      out.push(p);
    };

    // 1. Personalized from view history
    const history = getViewHistory();
    if (history.length && out.length < limit) {
      try {
        const anchor = history[0];
        const url = `/recommendations/products.json?product_id=${encodeURIComponent(anchor.id)}&intent=related&limit=${CONFIG.apiLimit}`;
        const data = await fetchJSON(url);
        const recs = (data && (data.products || data.recommendations)) || [];
        recs.forEach(accept);
      } catch (e) {}
    }

    // 2. Bestsellers collection (try common handles, sort by best-selling)
    if (out.length < limit) {
      const candidates = ['bestsellers', 'best-sellers', 'top-sellers', 'featured', 'all'];
      for (const handle of candidates) {
        if (out.length >= limit) break;
        try {
          const data = await fetchJSON(`/collections/${handle}/products.json?limit=24&sort_by=best-selling`);
          const list = (data && data.products) || [];
          if (!list.length) continue;
          list.forEach(accept);
          if (out.length >= limit) break;
        } catch (e) {}
      }
    }

    // 3. Newest fallback (cold start, no history, no bestsellers collection)
    if (out.length < limit) {
      try {
        const data = await fetchJSON('/products.json?limit=24');
        const list = (data && data.products) || [];
        list.forEach(accept);
      } catch (e) {}
    }

    return out.slice(0, limit);
  }

  // ==================== Product helpers ====================
  function productPrice(p) {
    if (typeof p.price === 'number' && p.price > 100) return p.price;
    if (p.variants && p.variants[0] && p.variants[0].price) {
      const v = p.variants[0].price;
      return typeof v === 'string' ? Math.round(parseFloat(v) * 100) : v;
    }
    if (typeof p.price === 'string') return Math.round(parseFloat(p.price.replace(/[^0-9.]/g, '')) * 100);
    return Math.round(parseFloat(p.price || 0) * 100);
  }
  function productImage(p) {
    if (p.featured_image) return typeof p.featured_image === 'string' ? p.featured_image : (p.featured_image.url || '');
    if (p.images && p.images[0]) return typeof p.images[0] === 'string' ? p.images[0] : (p.images[0].src || p.images[0].url);
    return '';
  }
  function productUrl(p) { return p.url || (p.handle ? `/products/${p.handle}` : '#'); }
  function firstAvailableVariantId(p) {
    if (!p.variants || !p.variants.length) return null;
    // Prefer explicitly-available variants; if none flagged, take the first
    // (Shopify's product-recs API may omit the `available` field).
    const avail = p.variants.find(v => v.available === true)
               || p.variants.find(v => typeof v.available === 'undefined');
    return (avail || p.variants[0]).id;
  }

  // Normalize product_type strings for matching (case-insensitive, trimmed)
  function normType(t) { return String(t || '').toUpperCase().trim(); }

  // Filter API recs to only those whose product_type appears in the allowed list.
  // Allowed = complementary_types ∪ cross_sell_types from CATEGORY_RULES for the anchor.
  // IMPORTANT: Shopify's /recommendations/products.json frequently omits or
  // returns an empty string for `product_type`. Treat empty as "unknown but
  // not disallowed" and let it through — otherwise we silently drop the entire
  // API response and fall back to suggest.json (which has its own variants[]
  // problem). This was the root cause of the v2.4 mobile "Sold out" bug.
  function filterByAllowedTypes(products, anchorType) {
    const rule = CATEGORY_RULES[normType(anchorType)];
    if (!rule) return products; // unknown anchor type — don't filter
    const allowed = new Set([
      ...(rule.complementary_types || []),
      ...(rule.cross_sell_types || [])
    ].map(normType));
    return products.filter(p => {
      const t = normType(p.product_type || p.type);
      if (!t) return true; // blank product_type from rec API — keep it
      return allowed.has(t);
    });
  }

  function productCardHTML(p, surface) {
    const img = productImage(p);
    const imgTag = img ? `<img loading="lazy" src="${escapeHtml(img)}" alt="${escapeHtml(p.title)}" width="120" height="120">` : '<div class="vv-upsell__no-image"></div>';
    const priceCents = productPrice(p);
    const variantId = firstAvailableVariantId(p);
    // "Sold out" criteria — be conservative. We only flag a card as sold out
    // when we have an EXPLICIT availability signal. Missing variants[] is NOT
    // the same as sold out (Shopify's suggest.json never returns variants);
    // in that case we render a "View" link to the PDP instead of an Add button.
    const hasVariants = !!(p.variants && p.variants.length);
    const explicitlySoldOut = (p.available === false)
      || (hasVariants && !variantId)
      || (hasVariants && p.variants.every(v => v && v.available === false));
    const url = productUrl(p);
    const dataJson = escapeHtml(JSON.stringify({
      id: p.id, title: p.title, product_type: p.product_type || p.type || '', vendor: p.vendor || '',
      available: p.available !== false,
      variants: [{ id: variantId || null, price: priceCents, available: !!variantId && !explicitlySoldOut }]
    }));

    // Three button states:
    //   1) Explicit OOS                  → disabled "Sold out"
    //   2) Missing variant id (rec API)  → "View" link to PDP (still useful, never wrong)
    //   3) Normal in-stock               → "+ Add" with variant id
    let actionHTML;
    if (explicitlySoldOut) {
      actionHTML = '<button class="vv-upsell__btn vv-upsell__btn--disabled" disabled aria-disabled="true">Sold out</button>';
    } else if (!variantId) {
      actionHTML = `<a class="vv-upsell__btn vv-upsell__btn--link" href="${escapeHtml(url)}" data-vv-surface="${surface || ''}">View</a>`;
    } else {
      actionHTML = `<button type="button" class="vv-upsell__btn" data-vv-add="${variantId}" data-vv-surface="${surface || ''}">+ Add</button>`;
    }

    return `
      <li class="vv-upsell__item" data-product-id="${p.id}" data-vv-product="${dataJson}">
        <a class="vv-upsell__media" href="${escapeHtml(url)}" data-vv-track-click="${p.id}">${imgTag}</a>
        <div class="vv-upsell__body">
          <a class="vv-upsell__title" href="${escapeHtml(url)}">${escapeHtml(p.title)}</a>
          <div class="vv-upsell__price">${formatMoney(priceCents)}</div>
          ${actionHTML}
        </div>
      </li>`;
  }

  function listHTML(products, heading, surface) {
    if (!products.length) return '';
    return `
      <div class="vv-upsell" data-vv-upsell data-vv-surface="${escapeHtml(surface || '')}">
        <h3 class="vv-upsell__heading">${escapeHtml(heading)}</h3>
        <ul class="vv-upsell__list">${products.map(p => productCardHTML(p, surface)).join('')}</ul>
      </div>`;
  }

  // ==================== Free shipping + Free gift bars ====================
  function shippingBarHTML(totalCents) {
    const threshold = CONFIG.freeShippingThresholdCents;
    const remaining = Math.max(0, threshold - totalCents);
    const pct = Math.min(100, Math.round((totalCents / threshold) * 100));
    const unlocked = remaining === 0;
    return `
      <div class="vv-ship-bar ${unlocked ? 'vv-ship-bar--unlocked' : ''}" data-vv-ship-bar>
        <div class="vv-ship-bar__msg">${unlocked ? '🎉 You\'ve unlocked <strong>FREE shipping</strong>!' : `Add <strong>${formatMoney(remaining)}</strong> more for FREE shipping`}</div>
        <div class="vv-ship-bar__track"><div class="vv-ship-bar__fill" style="width:${pct}%"></div></div>
      </div>`;
  }

  function freeGiftBarHTML(totalCents) {
    if (!CONFIG.freeGiftVariantId || CONFIG.freeGiftThresholdCents <= 0) return '';
    const threshold = CONFIG.freeGiftThresholdCents;
    const remaining = Math.max(0, threshold - totalCents);
    const pct = Math.min(100, Math.round((totalCents / threshold) * 100));
    const unlocked = remaining === 0;
    return `
      <div class="vv-gift-bar ${unlocked ? 'vv-gift-bar--unlocked' : ''}" data-vv-gift-bar>
        <div class="vv-gift-bar__msg">${unlocked ? `🎁 You\'ve unlocked your FREE ${escapeHtml(CONFIG.freeGiftTitle)}!` : `Spend <strong>${formatMoney(remaining)}</strong> more for a FREE ${escapeHtml(CONFIG.freeGiftTitle)}`}</div>
        <div class="vv-gift-bar__track"><div class="vv-gift-bar__fill" style="width:${pct}%"></div></div>
      </div>`;
  }

  // ==================== Add-to-cart handler ====================
  async function handleAddClick(e) {
    const btn = e.target.closest('[data-vv-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const variantId = btn.getAttribute('data-vv-add');
    const surface = btn.getAttribute('data-vv-surface') || 'unknown';
    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    // Analytics: click
    const itemEl = btn.closest('.vv-upsell__item');
    let productData = null;
    if (itemEl && itemEl.dataset.vvProduct) {
      try { productData = JSON.parse(itemEl.dataset.vvProduct); } catch (e) {}
    }
    if (window.VVAnalytics && productData) {
      window.VVAnalytics.trackClick(productData, surface);
    }

    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
      });
      if (!r.ok) throw new Error('Add failed');
      btn.textContent = '✓ Added';

      // Analytics: add
      if (window.VVAnalytics && productData) {
        window.VVAnalytics.trackAdd(productData, surface);
      }

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'vv-upsell', surface } }));
      if (window.theme && window.theme.cart && typeof window.theme.cart.getCart === 'function') {
        window.theme.cart.getCart();
      }
      setTimeout(refresh, 350);
    } catch (err) {
      console.error('[VVUpsell]', err);
      btn.textContent = 'Try again';
    } finally {
      setTimeout(() => { btn.disabled = false; btn.textContent = original; }, 1500);
    }
  }

  // ==================== Surface renderers ====================
  async function renderActiveCart(container) {
    if (!container) return;
    const cart = await fetchCart();
    if (!cart.items.length) return renderEmptyCart(container);
    container.innerHTML = shippingBarHTML(cart.total_price) + freeGiftBarHTML(cart.total_price) +
      '<div class="vv-upsell__loading">Finding pairings…</div>';
    const excludeIds = cart.items.map(i => i.product_id);
    const anchor = cart.items[0];
    const recs = await getRecommendations(anchor.product_id, anchor.product_type, excludeIds);
    const heading = (CATEGORY_RULES[(anchor.product_type || '').toUpperCase()] || {}).message || 'Frequently bought together';
    container.innerHTML = shippingBarHTML(cart.total_price) + freeGiftBarHTML(cart.total_price) + listHTML(recs, heading, 'cart-drawer');
    document.dispatchEvent(new CustomEvent('vv:upsell:rendered'));

    // Auto-add free gift if threshold unlocked
    await maybeAddFreeGift(cart);
  }

  async function renderEmptyCart(container) {
    if (!container) return;
    container.innerHTML = '<div class="vv-upsell__loading">Loading suggestions…</div>';
    const bestsellers = await getBestsellers();
    const hasHistory = getViewHistory().length > 0;
    const heading = hasHistory ? '✨ Picked for you' : '🔥 Customer Favourites';
    container.innerHTML = listHTML(bestsellers, heading, 'empty-cart');
    document.dispatchEvent(new CustomEvent('vv:upsell:rendered'));
  }

  async function renderPDP() {
    const container = document.querySelector('[data-vv-pdp-fbt]');
    if (!container) return;
    const productId = container.dataset.productId;
    const productType = container.dataset.productType;
    if (!productId) return;
    // Track this product view so empty-cart can personalize later
    pushViewHistory(productId, productType);
    const recs = await getRecommendations(productId, productType, [parseInt(productId, 10)]);
    const heading = container.querySelector('.vv-pdp-fbt__heading')?.textContent || 'Frequently bought together';
    container.innerHTML = `<h2 class="vv-pdp-fbt__heading">${escapeHtml(heading)}</h2>` + listHTML(recs, '', 'pdp');
    // Remove duplicated heading inside the list
    const innerHeading = container.querySelector('.vv-upsell .vv-upsell__heading');
    if (innerHeading) innerHeading.remove();
    document.dispatchEvent(new CustomEvent('vv:upsell:rendered'));
  }

  async function renderThankYou() {
    const container = document.querySelector('[data-vv-thankyou-upsell]');
    if (!container) return;
    const anchorId = container.dataset.anchorProductId;
    const limit = parseInt(container.dataset.limit, 10) || 4;
    if (!anchorId) {
      const best = await getBestsellers();
      container.innerHTML = listHTML(best.slice(0, limit), '', 'thankyou');
    } else {
      const recs = await getRecommendations(anchorId, '', [parseInt(anchorId, 10)]);
      container.innerHTML = listHTML(recs.slice(0, limit), '', 'thankyou');
    }
    document.dispatchEvent(new CustomEvent('vv:upsell:rendered'));
  }

  // ==================== Pop-up ====================
  function popupCooldownActive() {
    try {
      const last = parseInt(localStorage.getItem('vv_popup_last') || '0', 10);
      return Date.now() - last < CONFIG.popupCooldownMin * 60 * 1000;
    } catch (e) { return false; }
  }
  function popupMarkShown() {
    try { localStorage.setItem('vv_popup_last', String(Date.now())); } catch (e) {}
  }

  async function maybeShowPopup(lastAddedItem) {
    if (!CONFIG.popupEnabled) return;
    if (popupCooldownActive()) return;
    const popup = document.getElementById('VVPopupUpsell');
    if (!popup) return;
    const body = popup.querySelector('[data-vv-popup-body]');
    if (!body) return;

    body.innerHTML = '<div class="vv-popup__loading">Finding the perfect pairing…</div>';
    const recs = await getRecommendations(lastAddedItem.product_id, lastAddedItem.product_type, [lastAddedItem.product_id]);
    const top = recs.slice(0, 2);
    if (!top.length) return;
    body.innerHTML = listHTML(top, '', 'popup');
    popup.hidden = false;
    requestAnimationFrame(() => popup.classList.add('vv-popup--open'));
    popupMarkShown();
    document.dispatchEvent(new CustomEvent('vv:upsell:rendered'));

    if (window.VVAnalytics) window.VVAnalytics.track('vv_popup_shown', { anchor_product_id: String(lastAddedItem.product_id) });
  }

  function closePopup() {
    const popup = document.getElementById('VVPopupUpsell');
    if (!popup) return;
    popup.classList.remove('vv-popup--open');
    setTimeout(() => { popup.hidden = true; }, 250);
  }

  // ==================== Free gift auto-add ====================
  async function maybeAddFreeGift(cart) {
    if (!CONFIG.freeGiftVariantId) return;
    const giftVariantId = parseInt(CONFIG.freeGiftVariantId, 10);
    if (!giftVariantId) return;
    const hasGift = cart.items.some(i => i.variant_id === giftVariantId);
    const unlocked = cart.total_price >= CONFIG.freeGiftThresholdCents;

    if (unlocked && !hasGift) {
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: giftVariantId, quantity: 1, properties: { '_free_gift': 'true' } }] })
        });
      } catch (e) {}
    } else if (!unlocked && hasGift) {
      // Remove gift if subtotal dropped below threshold
      const giftLine = cart.items.find(i => i.variant_id === giftVariantId);
      if (giftLine) {
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: giftLine.key, quantity: 0 })
          });
        } catch (e) {}
      }
    }
  }

  // ==================== Refresh orchestration ====================
  async function refresh() {
    const cart = await fetchCart();
    const active = document.querySelector('[data-vv-cart-upsell="active"]');
    const empty = document.querySelector('[data-vv-cart-upsell="empty"]');
    if (active) {
      active.style.display = cart.items.length === 0 ? 'none' : '';
      if (cart.items.length > 0) await renderActiveCart(active);
    }
    if (empty) {
      empty.style.display = cart.items.length > 0 ? 'none' : '';
      if (cart.items.length === 0) await renderEmptyCart(empty);
    }
  }

  // ==================== Cart event interception (for popup) ====================
  function interceptAddToCart() {
    // Patch fetch to detect /cart/add.js calls so we can trigger popup
    const origFetch = window.fetch;
    window.fetch = function (url, options) {
      const result = origFetch.apply(this, arguments);
      if (typeof url === 'string' && url.includes('/cart/add')) {
        result.then(async (resp) => {
          if (!resp.ok) return;
          // Don't show popup for internal upsell adds
          const isInternal = options && options.body && (
            (typeof options.body === 'string' && options.body.includes('_free_gift'))
          );
          if (isInternal) return;

          // Trigger popup with newest cart item
          setTimeout(async () => {
            const cart = await fetchCart();
            const last = cart.items[cart.items.length - 1];
            if (last) maybeShowPopup(last);
            refresh();
          }, 200);
        }).catch(() => {});
      }
      return result;
    };
  }

  // ==================== Init ====================
  function init() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-vv-popup-close]')) { closePopup(); return; }
      handleAddClick(e);
    }, true);

    document.addEventListener('cart:updated', refresh);
    document.addEventListener('ajaxCart.afterCartLoad', refresh);
    document.addEventListener('cart.requestComplete', refresh);

    refresh();
    renderPDP();
    renderThankYou();
    interceptAddToCart();

    const drawer = document.getElementById('CartDrawer');
    if (drawer) {
      new MutationObserver(() => {
        if (drawer.classList.contains('is-active') || drawer.classList.contains('drawer--is-open') || drawer.getAttribute('aria-hidden') === 'false') refresh();
      }).observe(drawer, { attributes: true, attributeFilter: ['class', 'aria-hidden'] });
    }
  }

  window.VVUpsell = { renderActiveCart, renderEmptyCart, renderPDP, renderThankYou, refresh, closePopup };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
